import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import EmptyState from "./EmptyState";
import Tooltip from "./Tooltip";
import InfoIcon from "./InfoIcon";
import {
  TOPIC_CLUSTERS,
  getAxisScoresForCluster,
  getClusterByBackendId
} from "../constants/topicClusters";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toNormSigned(signed) {
  const clamped = clamp(signed, -100, 100);
  return (clamped + 100) / 200; // 0..1
}

/** Display axis scores as positive percentages (e.g. -82 → 82%). */
function axisScoreToDisplayPercent(signed) {
  if (typeof signed !== "number" || Number.isNaN(signed)) return 0;
  return Math.round(Math.abs(signed));
}

/** Negative = Internal (left); positive = External (right). */
function internalExternalPoleLabel(score) {
  if (typeof score !== "number" || Number.isNaN(score)) return "—";
  if (score > 0) return "External";
  if (score < 0) return "Internal";
  return "Balanced";
}

/** Negative = Strategic (top); positive = Operational (bottom). */
function operationalStrategicPoleLabel(score) {
  if (typeof score !== "number" || Number.isNaN(score)) return "—";
  if (score > 0) return "Operational";
  if (score < 0) return "Strategic";
  return "Balanced";
}

function bubbleDiameterFromPercent(percent, maxPercent) {
  if (typeof percent !== "number" || Number.isNaN(percent)) return 5;
  if (typeof maxPercent !== "number" || Number.isNaN(maxPercent) || maxPercent <= 0) {
    return 5;
  }

  // If the most visible topic is still <10%, absolute thresholds collapse all dots to 5px.
  // In that case, scale bubbles relatively so users still see size differentiation.
  if (maxPercent < 10) {
    if (percent <= 0) return 5;
    const t = clamp(percent / maxPercent, 0, 1);
    return 5 + t * 20; // 5..25
  }

  // Spec behavior for normal ranges
  if (percent < 10) return 5;
  if (percent >= 100) return 25;
  // Linear map: 10% -> 10px, 100% -> 25px
  return 10 + ((percent - 10) * 15) / 90;
}

const VIEWPORT_PAD = 8;

const TopicCloudChart = ({ data }) => {
  const [hoveredKey, setHoveredKey] = useState(null);
  /** Viewport coordinates for fixed tooltip (avoids clipping by chart overflow). */
  const [tooltipPointer, setTooltipPointer] = useState({ x: 0, y: 0 });
  const topicTooltipRef = useRef(null);
  const [hiddenClusters, setHiddenClusters] = useState(new Set());
  const containerRef = useRef(null);
  const chartAreaRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1000, height: 300 });

  const toggleCluster = (numericId) => {
    setHiddenClusters((prev) => {
      const next = new Set(prev);
      if (next.has(numericId)) next.delete(numericId);
      else next.add(numericId);
      return next;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        const height = chartAreaRef.current
          ? chartAreaRef.current.getBoundingClientRect().height
          : 300;
        setDimensions({ width, height });
      }
    };
    handleResize();
    const resizeObserver = new window.ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  const chartModel = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        points: [],
        clusterColors: {},
      };
    }

    const globalTotalFreq = data.reduce(
      (s, topic) => s + (topic.frequency || topic.value || 1),
      0
    );

    const clusterColors = {};
    const clusterAllPoints = {};
    TOPIC_CLUSTERS.forEach((c) => {
      clusterColors[c.numericId] = {
        color: c.color,
        bg: `${c.color}20`,
        name: c.name,
        description: c.description
      };
      clusterAllPoints[c.numericId] = [];
    });

    const toSignedNorm = (signed) => {
      const clamped = clamp(signed, -100, 100);
      return (clamped + 100) / 200; // 0..1 (0 = -100, 1 = +100)
    };

    data.forEach((topic, index) => {
      const topicLabel = topic.topic || topic.name || "";
      const clusterDef =
        getClusterByBackendId(topic.category) ||
        getClusterByBackendId(topic.cluster) ||
        TOPIC_CLUSTERS[0];

      const fallbackAxes = getAxisScoresForCluster(clusterDef.id);

      const hasX = typeof topic.internalExternal === "number";
      const hasY = typeof topic.operationalStrategic === "number";

      const xSigned = hasX ? topic.internalExternal : fallbackAxes.internalExternal;
      const ySigned = hasY ? topic.operationalStrategic : fallbackAxes.operationalStrategic;

      const estimatedAxis = !(hasX && hasY);

      const frequency = topic.frequency || topic.value || 1;
      const percent = 0;

      clusterAllPoints[clusterDef.numericId].push({
        topic: topicLabel,
        frequency,
        percent,
        cluster: clusterDef.numericId,
        clusterInfo: clusterDef,
        internalExternal: xSigned,
        operationalStrategic: ySigned,
        estimatedAxis,
        layoutKey: `${topicLabel}-${clusterDef.numericId}-${index}`,
        clusterNumericId: clusterDef.numericId,
        _xNorm: toSignedNorm(xSigned),
        _yNorm: toSignedNorm(ySigned),
      });
    });

    // Show every aggregated topic (no per-cluster cap). Sort within cluster:
    // prefer embedding-scored axes, then by frequency.
    const points = [];
    TOPIC_CLUSTERS.forEach((c) => {
      const pts = clusterAllPoints[c.numericId] || [];
      pts.sort((a, b) => {
        if (a.estimatedAxis !== b.estimatedAxis) return a.estimatedAxis ? 1 : -1;
        return b.frequency - a.frequency || a.topic.localeCompare(b.topic);
      });
      pts.forEach((p) => points.push(p));
    });

    // Percent = share of total topic weight across the whole map (matches tooltip).
    if (globalTotalFreq > 0) {
      points.forEach((p) => {
        p.percent = (p.frequency / globalTotalFreq) * 100;
      });
    }

    const maxPercent = points.reduce((m, p) => Math.max(m, p.percent || 0), 0);

    return { points, clusterColors, maxPercent };
  }, [data]);

  const { points: processedData, clusterColors, maxPercent } = chartModel;

  const visiblePoints = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    if (!hiddenClusters || hiddenClusters.size === 0) return processedData;
    return processedData.filter((p) => !hiddenClusters.has(p.cluster));
  }, [processedData, hiddenClusters]);

  const visibleMaxPercent = useMemo(() => {
    if (!visiblePoints || visiblePoints.length === 0) return 0;
    return visiblePoints.reduce((m, p) => Math.max(m, p.percent || 0), 0);
  }, [visiblePoints]);

  // If the hovered point is no longer visible (cluster hidden), clear hover.
  useEffect(() => {
    if (!hoveredKey) return;
    const stillVisible = visiblePoints.some((p) => p.layoutKey === hoveredKey);
    if (!stillVisible) setHoveredKey(null);
  }, [hoveredKey, visiblePoints]);

  const padding = 0;
  const chartWidth = Math.max(dimensions.width - padding * 2, 100);
  const chartHeight = Math.max(dimensions.height - padding * 2, 100);
  // Keep bubbles from being clipped on chart boundaries.
  // Max hover radius is capped at 12.5px below, so 14px is a safe inset.
  const plotPad = 14;
  const innerWidth = Math.max(chartWidth - plotPad * 2, 10);
  const innerHeight = Math.max(chartHeight - plotPad * 2, 10);
  const xCenter = plotPad + innerWidth / 2;
  const yCenter = plotPad + innerHeight / 2;

  const realPointsCount = visiblePoints.filter((p) => !p.estimatedAxis).length;

  // hoveredRow + useLayoutEffect MUST run before any conditional return (Rules of Hooks).
  const hoveredRow = useMemo(
    () => processedData.find((d) => d.layoutKey === hoveredKey),
    [processedData, hoveredKey]
  );

  useLayoutEffect(() => {
    if (!hoveredKey || !hoveredRow || !topicTooltipRef.current) return;
    const el = topicTooltipRef.current;
    const left = tooltipPointer.x + 10;
    const top = tooltipPointer.y - 28;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    const rect = el.getBoundingClientRect();
    let adjLeft = left;
    let adjTop = top;
    if (rect.right > window.innerWidth - VIEWPORT_PAD) {
      adjLeft -= rect.right - (window.innerWidth - VIEWPORT_PAD);
    }
    if (rect.left < VIEWPORT_PAD) {
      adjLeft += VIEWPORT_PAD - rect.left;
    }
    if (rect.bottom > window.innerHeight - VIEWPORT_PAD) {
      adjTop -= rect.bottom - (window.innerHeight - VIEWPORT_PAD);
    }
    if (rect.top < VIEWPORT_PAD) {
      adjTop += VIEWPORT_PAD - rect.top;
    }
    el.style.left = `${adjLeft}px`;
    el.style.top = `${adjTop}px`;
  }, [hoveredKey, hoveredRow, tooltipPointer]);

  if (!data || data.length === 0 || processedData.length === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Topic clusters</h3>
          <Tooltip
            content="Each dot is a topic positioned by embedding-based axis scores. Dot size represents the topic’s share of mentions in the current view. Use the legend to show/hide clusters."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
        <EmptyState
          variant="no-data"
          title="No topic data available"
          description="There's no topic distribution data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  if (realPointsCount === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Topic clusters</h3>
          <Tooltip
            content="Each dot is a topic positioned by embedding-based axis scores. Dot size represents the topic’s share of mentions in the current view. Use the legend to show/hide clusters."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
        <EmptyState
          variant="no-data"
          title="Axis scores not available yet"
          description="This report doesn’t include per-topic axis scores (Internal↔External, Operational↔Strategic), so the clustering map would be misleading. Re-sync to regenerate topics with axis scoring enabled."
          actionButton={null}
        />
      </div>
    );
  }

  const renderLegend = () => (
    <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-2">
      {TOPIC_CLUSTERS.map((c) => {
        const hidden = hiddenClusters.has(c.numericId);
        return (
          <button
            key={c.numericId}
            type="button"
            onClick={() => toggleCluster(c.numericId)}
            className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
            style={{ opacity: hidden ? 0.35 : 1 }}
            title={c.description}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: c.color }}
            />
            <span className={hidden ? "text-grey-60 line-through" : "text-grey-60"}>
              {c.name}
            </span>
          </button>
        );
      })}
    </div>
  );

  const topicTooltipPortal =
    hoveredKey && hoveredRow && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={topicTooltipRef}
            className="fixed bg-white-f border border-grey-30 rounded-lg shadow-[0px_1px_5px_0px_rgba(28,27,34,0.1)] overflow-hidden z-[10000] pointer-events-none w-[260px] max-w-[260px] font-schibsted text-black-0"
            style={{ left: 0, top: 0 }}
          >
            <div className="border-b border-grey-20 px-4 py-3">
              <p className="font-semibold text-base leading-[22px] tracking-[-0.5px] break-words">
                {hoveredRow.topic}
              </p>
            </div>
            <div className="border-b border-grey-20 flex flex-col gap-1 px-4 py-3">
              <div className="flex gap-1 items-baseline h-6 w-full text-sm leading-[22px] tracking-[-0.5px]">
                <span className="flex-1 min-w-0 font-normal">Cluster</span>
                <span className="flex items-center gap-1 shrink-0">
                  <span
                    className="rounded-[2px] shrink-0 w-2.5 h-2.5"
                    style={{ backgroundColor: hoveredRow.clusterInfo?.color || "#8a5cf5" }}
                    aria-hidden
                  />
                  <span className="font-medium whitespace-nowrap">
                    {hoveredRow.clusterInfo?.name || "—"}
                  </span>
                </span>
              </div>
              <div className="flex gap-1 items-baseline h-6 w-full text-sm leading-[22px] tracking-[-0.5px]">
                <span className="flex-1 min-w-0 font-normal">Mentions</span>
                <span className="font-medium whitespace-nowrap">
                  {Math.round(hoveredRow.percent)}%
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-1 px-4 py-3">
              <p className="font-medium text-xs leading-5 tracking-[0.5px] uppercase text-grey-60 whitespace-nowrap">
                Position
              </p>
              <div className="flex gap-1 items-baseline h-6 w-full text-sm leading-[22px] tracking-[-0.5px]">
                <span className="flex-1 min-w-0 font-normal">
                  {internalExternalPoleLabel(hoveredRow.internalExternal)}
                </span>
                <span className="font-medium whitespace-nowrap">
                  {axisScoreToDisplayPercent(hoveredRow.internalExternal)}%
                </span>
              </div>
              <div className="flex gap-1 items-baseline h-6 w-full text-sm leading-[22px] tracking-[-0.5px]">
                <span className="flex-1 min-w-0 font-normal">
                  {operationalStrategicPoleLabel(hoveredRow.operationalStrategic)}
                </span>
                <span className="font-medium whitespace-nowrap">
                  {axisScoreToDisplayPercent(hoveredRow.operationalStrategic)}%
                </span>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={containerRef}
      className="bg-white-f border border-grey-20 shadow-subtle rounded-lg px-5 py-4 flex flex-col h-[440px]"
    >
      {topicTooltipPortal}
      <div className="flex items-center">
        <p className="text-sm font-normal text-black-0 tracking-[-0.5px]">
          Topic clusters
        </p>
      </div>

      <div ref={chartAreaRef} className="flex-1 min-h-0">
        <div className="h-full w-full flex gap-3 items-stretch">
          {/* Left label */}
          <div className="h-full w-4 flex items-center justify-center">
            <div className="-rotate-90 text-xs text-grey-60 tracking-[-0.5px]">
              Internal
            </div>
          </div>

          {/* Center column */}
          <div className="flex-1 h-full flex flex-col">
            <div className="py-2 flex justify-center">
              <div className="text-xs text-grey-60 tracking-[-0.5px]">
                Strategic
              </div>
            </div>

            <div className="relative flex-1 border border-grey-20 rounded-[0px] overflow-hidden">
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${chartWidth + padding * 2} ${chartHeight + padding * 2}`}
                preserveAspectRatio="none"
              >
                {/* Horizontal dashed grid (with center black) */}
                {Array.from({ length: 7 }, (_, i) => {
                  const y = plotPad + (innerHeight / 6) * i;
                  const isCenter = i === 3;
                  return (
                    <line
                      key={`h-${i}`}
                      x1={plotPad}
                      y1={y}
                      x2={chartWidth - plotPad}
                      y2={y}
                      stroke={isCenter ? "#04101A" : "#F0F2F4"}
                      strokeWidth="1"
                      strokeDasharray="4,4"
                    />
                  );
                })}

                {/* Center vertical dashed axis (black) */}
                <line
                  x1={xCenter}
                  y1={plotPad}
                  x2={xCenter}
                  y2={chartHeight - plotPad}
                  stroke="#04101A"
                  strokeWidth="1"
                  strokeDasharray="4,4"
                />

                {visiblePoints.map((topic) => {
                  const cx = plotPad + toNormSigned(topic.internalExternal) * innerWidth;
                  const cy = plotPad + toNormSigned(topic.operationalStrategic) * innerHeight;
                  const isHovered = hoveredKey === topic.layoutKey;
                  const clusterInfo = clusterColors[topic.cluster];

                  const diameter = bubbleDiameterFromPercent(
                    topic.percent,
                    visibleMaxPercent > 0 ? visibleMaxPercent : maxPercent
                  );
                  const r = diameter / 2;

                  return (
                    <circle
                      key={topic.layoutKey}
                      cx={cx}
                      cy={cy}
                      r={isHovered ? Math.min(r + 2, 12.5) : r}
                      fill={clusterInfo.color}
                      fillOpacity={isHovered ? 0.9 : 0.7}
                      stroke={clusterInfo.color}
                      strokeWidth="1"
                      className="cursor-pointer transition-all duration-200"
                      onMouseEnter={(e) => {
                        setHoveredKey(topic.layoutKey);
                        setTooltipPointer({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => {
                        setTooltipPointer({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseLeave={() => {
                        setHoveredKey(null);
                        setTooltipPointer({ x: 0, y: 0 });
                      }}
                    />
                  );
                })}
              </svg>
            </div>

            <div className="py-2 flex justify-center">
              <div className="text-xs text-grey-60 tracking-[-0.5px]">
                Operational
              </div>
            </div>

            {/* Legend under the axis label (outside overflow-hidden so it can't be clipped) */}
            {renderLegend()}
          </div>

          {/* Right label */}
          <div className="h-full w-4 flex items-center justify-center">
            <div className="-rotate-90 text-xs text-grey-60 tracking-[-0.5px]">
              External
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicCloudChart;
